/**
 * 기종/옵션 선택 UI — 업종 > 분류1 > 분류2 3단 셀렉트 + 전고·차량번호·옵션.
 *
 * 기종 마스터는 `$j.F.Car` 가 들고 있다 — 서버가 `/asset/bootstrap` 으로 심어 준 `G5.CARMASTER`.
 *
 * [상태는 DOM 이 정본이다]
 * 예전에는 고른 값을 인스턴스 필드(`this.depth2` 등)에도 들고 있었는데, 사용자가 <select> 를
 * **건드렸을 때만** 갱신돼서 초기 렌더 직후 값을 읽으면 비어 있었다. 지금은 그리기만 하고
 * 읽을 땐 항상 DOM 을 본다. `initialize(data.v)` 로 받은 초기값은 `sel` 에 두는데, 이건
 * **그릴 때 한 번 쓰는 값**이지 현재 선택이 아니다.
 *
 * [싱글턴이다]
 * `$j.F.CarSelect` 는 인스턴스 하나를 6곳이 돌려 쓴다. 그래서
 *  - 핸들러는 전부 네임스페이스(`.j-carselect`)로 달고 `initialize()` 시작에서 떼어낸다
 *  - 옵션 버튼 클릭은 옵션영역에 **위임**한다 (예전엔 `$('.option-btn')` 전역 셀렉터라
 *    다른 화면의 버튼까지 잡았고, 토글할 때마다 핸들러가 한 겹씩 쌓였다)
 *
 * idList 에 없는 칸은 조용히 건너뛴다 (오더 기종 상세검색은 3단만 쓴다 — F/Order/CarDetail).
 *
 * [범위모드]
 * idList 에 종료칸을 주면 그 축이 범위가 된다 — `depth11`(톤수 종료)·`depth22`(규격 종료).
 * 장비**검색** 모달만 주고, 장비**등록** 화면은 안 준다 (장비 하나를 고르는 자리라 범위가 없다).
 *
 *   스카이   depth1~depth11 톤수 · depth2~depth22 미터   → `100~120|16~34`
 *   사다리   depth1~depth11 톤수 · depth2 층(이상)        → `200~230|8`
 *            └ 층 범위는 쓰지 않는다. 같은 층을 여러 톤수가 커버해서 층만으론 못 좁히고,
 *              좁히는 일은 톤수 범위가 한다. 그래서 사다리에서는 depth22 를 숨긴다
 *   그 외    depth1 유형 · depth2~depth22 규격            → `520~526`
 *
 * ⚠ 미터 선택지는 **톤수 범위가 커버하는 것만** 보여준다. 서버(CarMatchSql::rangeSql)는
 *   스카이의 코드부를 보지 않고 미터로만 거른다 — 스카이는 size_list 가 안 겹쳐
 *   미터가 코드를 유일하게 정하기 때문이다. 그래서 미터가 톤수 범위를 벗어나면
 *   화면과 검색결과가 어긋난다. 반대로 사다리는 코드부를 보므로 톤수 범위가 그대로 먹는다.
 */

/** 핸들러 네임스페이스 — 남의 핸들러를 건드리지 않고 이 모듈 것만 떼어낸다 */
const NS = '.j-carselect';

export default class CarSelect {
    constructor() {
        this.idList = {};
        this.sel = {};   // 초기 선택값 (그리기 전용). 그린 뒤로는 DOM 이 정본이다
    }

    // ─────────────────────────────────────────────────────────────
    // 생명주기
    // ─────────────────────────────────────────────────────────────

    /**
     * @param data {{idList: Object, v?: Object}}
     *        idList — 칸별 element id. 없는 칸은 건너뛴다
     *        v      — 초기 선택값 (g5_cars 행이나 검색조건)
     */
    initialize(data) {
        this.destroy();                       // 같은 DOM 을 재사용하는 호출부 대비
        this.idList = (data && data.idList) || {};
        this.sel = this.resolve(data && data.v);
        this.draw();
        this.bind();
    }

    /** 이 모듈이 단 핸들러만 떼어낸다 */
    destroy() {
        Object.keys(this.idList).forEach((k) => this.$el(k).off(NS));
    }

    /**
     * 초기값 해석 — car_range 가 오면 3단 선택으로 되짚는다.
     *
     * 키 이름을 두 벌 받는다. 호출부가 갈려 있어서다 — 회원 장비행(g5_cars)은
     * `car_height`·`car_option`·`car_platenumber` 로 오고, 검색모달이 되돌려주는 조건객체는
     * `jungo`·`option`·`number` 로 온다. 한쪽만 보면 모달을 다시 열 때 전고·옵션이 사라진다.
     */
    resolve(v) {
        if (!v) return {};

        const carType = v.car_type || '';
        let code  = v.car_code,  code2  = v.car_code2;
        let meter = v.car_meter, meter2 = v.car_meter2;

        if (v.car_range) {   // 예: '520~526', '120|26~34'
            const info = $j.F.Car.getInfoFromCarRange(v) || { code_range: [], meter_range: [] };
            code   = info.code_range[0];
            code2  = info.code_range[1];
            meter  = info.meter_range[0];
            meter2 = info.meter_range[1];
        }

        const out = {
            car_type: carType,
            jungo:  v.car_height      ?? v.jungo  ?? '',
            number: v.car_platenumber ?? v.number ?? '',
            option: v.car_option      ?? v.option ?? '',
        };
        if ($j.F.Car.isMeterType(carType)) {
            out.depth1 = code; out.depth11 = code2;   // 톤수 범위
            out.depth2 = meter; out.depth22 = meter2; // 미터/층 범위
        } else {
            out.depth1 = this.findCa(code); out.depth2 = code; out.depth22 = code2;
        }
        return out;
    }

    bind() {
        // 업종이 바뀌면 아래 칸은 전부 다시 그린다 — 이전 선택은 버린다
        this.$el('car_type').on('change' + NS, () => {
            this.sel = {};
            this.drawDepth1();
            this.drawDepth2(1, true);
            this.drawDepth2(2, true);
            this.drawJungo();
            this.drawOption();
        });

        // 톤수(시작·종료) 가 바뀌면 미터 선택지가 달라진다 — 둘 다 다시 그린다
        this.$el('depth1').on('change' + NS, () => this.redrawSizes());
        this.$el('depth11').on('change' + NS, () => this.redrawSizes());

        // 위임 — 버튼은 다시 그려도 핸들러가 살아 있고, 이 영역 밖은 잡지 않는다
        this.$el('option').on('click' + NS, '.option-btn', (e) => {
            $(e.currentTarget).toggleClass('on');
        });
    }

    // ─────────────────────────────────────────────────────────────
    // 그리기
    // ─────────────────────────────────────────────────────────────

    draw() {
        this.drawType();
        this.drawDepth1();
        this.drawDepth2(1);
        this.drawDepth2(2);
        this.drawJungo();
        this.drawNumber();
        this.drawOption();
    }

    drawType() {
        const $el = this.$el('car_type');
        $el.empty().append('<option value="">업종선택</option>');
        $j.F.Car.types().forEach((v) => $el.append($('<option>').val(v).text(v)));
        if (this.sel.car_type) $el.val(this.sel.car_type);
    }

    /**
     * 분류1(스카이·사다리는 톤수, 나머지는 유형)을 그린다.
     * 톤수 종료칸(depth11)이 있으면 같은 목록으로 함께 그린다 — 스카이·사다리 전용.
     */
    drawDepth1() {
        const carType = this.getCarType();
        const isMeter = $j.F.Car.isMeterType(carType);

        // 톤수 범위는 스카이·사다리에만 있다. 나머지 업종은 분류2가 범위를 맡는다
        this.slot('depth11', isMeter);

        ['depth1', 'depth11'].forEach((key) => {
            const $el = this.$el(key);
            if (!$el.length || (key === 'depth11' && !isMeter)) return;

            $el.empty().append($('<option>').val('').text(this.getDepth1Title()));
            if (!carType) return;

            const seen = [];
            $j.F.Car.master().forEach((row) => {
                if (row.car_type !== carType) return;
                const v = isMeter ? row.code : row.ca1;   // 스카이·사다리는 기종코드, 나머지는 세부기종
                if (seen.includes(v)) return;
                seen.push(v);
                $el.append($('<option>').val(v).text(isMeter ? row.eng_name : row.ca1));
            });
            if (this.sel[key]) $el.val(this.sel[key]);
        });
    }

    /** 톤수(시작·종료)가 바뀐 뒤 미터/층 칸을 다시 그린다 */
    redrawSizes() {
        this.drawDepth2(1, true);
        this.drawDepth2(2, true);
    }

    /**
     * 범위모드인가 — idList 에 톤수 종료칸(depth11)을 준 화면.
     * 장비 **검색** 모달만 준다. 장비 **등록** 화면은 장비 하나를 고르는 자리라 범위가 없다.
     */
    isRangeMode() {
        return this.$el('depth11').length > 0;
    }

    /**
     * 톤수 범위에 속한 마스터 행 (스카이·사다리).
     * 종료를 안 고르면 시작 하나만 — 예전 단일선택과 같은 결과다.
     */
    tonRows() {
        const carType = this.getCarType();
        const fr = this.getDepth1();
        if (!fr) return [];
        const to = this.getDepth11() || fr;
        const lo = Math.min(+fr, +to);
        const hi = Math.max(+fr, +to);
        return $j.F.Car.master().filter((r) => r.car_type === carType && +r.code >= lo && +r.code <= hi);
    }

    /** 톤수 범위가 커버하는 미터(스카이)·층(사다리) 목록 — 오름차순, 중복 제거 */
    tonSizes() {
        const out = [];
        this.tonRows().forEach((r) => {
            r.size_list.split(',').forEach((s) => {
                if (s && !out.includes(s)) out.push(s);
            });
        });
        return out.sort((a, b) => a - b);
    }

    /**
     * 분류2를 그린다.
     * @param gubun 1: 시작칸, 2: 종료칸
     * @param isRefresh true면 기존 선택을 버리고 기본값(시작=첫칸, 종료=마지막칸)으로 맞춘다
     */
    drawDepth2(gubun = 1, isRefresh = false) {
        const key = gubun === 2 ? 'depth22' : 'depth2';
        const $el = this.$el(key);
        if (!$el.length) return;

        const carType = this.getCarType();

        // 사다리는 규격(층)으로 좁히지 않는다 — 같은 층을 여러 톤수가 커버해서 층으로는
        // 못 좁히고, 좁히는 일은 톤수 범위가 한다. 그래서 범위모드에선 칸을 통째로 감춘다.
        // 단 **장비 등록 화면(범위모드 아님)에서는 층이 필수**다 — g5_cars.car_meter 가 그 값이다.
        if (carType === '사다리' && (gubun === 2 || this.isRangeMode())) {
            $el.empty();              // 감추기만 하면 이전 업종에서 고른 값이 남아 car_range 에 샌다
            this.slot(key, false);
            return;
        }
        this.slot(key, true);

        $el.empty().append($('<option>').val('').text(this.getDepth2Title()));
        if (!carType) return;

        if ($j.F.Car.isMeterType(carType)) {
            // 분류2 = 미터(스카이)·층(사다리). 선택지는 **톤수 범위가 커버하는 것만** —
            // 벗어나면 서버가 미터로만 거르는 스카이에서 화면과 결과가 어긋난다
            const sizes = this.tonSizes();
            if (!sizes.length) return;
            sizes.forEach((s) => {
                if (carType === '스카이') {
                    $el.append($('<option>').val(s).text(`${s}m`));
                } else {
                    const m = $j.F.Car.floorToMeter(s);   // 사다리는 층으로 고르고 미터로 저장한다
                    $el.append($('<option>').val(m).text(`${s}층 (${m}m)`));
                }
            });
        } else {
            // 분류1 = 세부기종(ca1), 분류2 = 기종코드
            const depth1 = this.getDepth1();
            if (!depth1) return;
            $j.F.Car.master().forEach((row) => {
                if (row.car_type !== carType || row.ca1 !== depth1) return;
                $el.append($('<option>').val(row.code).text(row.eng_name));
            });
        }

        if (isRefresh) {
            // 시작은 범위의 처음, 종료는 범위의 끝
            if (gubun === 1) $el.find('option:eq(1)').prop('selected', true);
            else $el.val($el.find('option:last').val());
        } else if (this.sel[key]) {
            $el.val(this.sel[key]);
        }
    }

    /** 전고 — 리프트는 전고를 따지지 않아 선택지를 두지 않는다 */
    drawJungo() {
        const $el = this.$el('jungo');
        $el.show().empty().append('<option value="">전고</option>');

        const carType = this.getCarType();
        if (!carType) return;

        if (carType !== '리프트') {
            for (let i = 40; i >= 23; i--) {
                const v = (i / 10).toFixed(1);
                $el.append($('<option>').val(v).text(i === 40 ? `${v} 이하` : v));
            }
        }
        if (this.sel.jungo) $el.val(this.sel.jungo);
    }

    drawNumber() {
        this.$el('number').val(this.sel.number || '');
    }

    /** 옵션 버튼 — 켜짐은 `.on` 클래스가 정본이고, 값은 data-value 에 둔다 */
    drawOption() {
        const $el = this.$el('option');
        $el.empty();

        const carType = this.getCarType();
        if (!carType) return;

        const on = String(this.sel.option || '').split(',').filter((v) => v);
        $j.F.Car.options(carType).split(',').forEach((v) => {
            if (!v) return;
            $('<button type="button" class="option-btn"></button>')
                .attr('data-value', v)          // 표시문구와 값을 분리한다 (예전엔 innerHTML 을 값으로 썼다)
                .text(v)
                .toggleClass('on', on.includes(v))
                .appendTo($el);
        });
    }

    // ─────────────────────────────────────────────────────────────
    // 읽기 — 전부 DOM 에서 읽는다
    // ─────────────────────────────────────────────────────────────

    getCarType() { return this.val('car_type'); }
    getDepth1()  { return this.val('depth1'); }
    getDepth11() { return this.val('depth11'); }
    getDepth2()  { return this.val('depth2'); }
    getDepth22() { return this.val('depth22'); }
    getJungo()   { return this.val('jungo'); }
    getNumber()  { return this.val('number'); }

    /** 켜진 옵션의 CSV */
    getOption() {
        return this.$el('option').find('.option-btn.on')
            .map((i, el) => $(el).attr('data-value')).get().join(',');
    }

    /** 선택을 car_range 문자열로 (`120|26~34`, `520~526`). 고른 게 없으면 빈 문자열 */
    getCarRange() {
        const carType = this.getCarType();
        const d2 = this.getDepth2();
        const d22 = this.getDepth22();

        if ($j.F.Car.isMeterType(carType)) {
            const c1 = this.getDepth1();
            if (!c1) return '';

            // 톤수 범위. 종료를 안 골랐거나 같으면 단일 코드로 — 예전 형식과 그대로 호환된다.
            // 거꾸로 고르면(종료<시작) 뒤집는다 — 서버는 between 이라 그대로 두면 0건이 된다
            const c2 = this.getDepth11();
            let r = String(c1);
            if (c2 && c2 !== c1) {
                r = (+c2 < +c1) ? `${c2}~${c1}` : `${c1}~${c2}`;
            }

            if (d2) {
                let m1 = d2, m2 = (carType === '사다리') ? '' : d22;   // 사다리는 층 '이상' 하나로 끝낸다
                if (m2 && +m2 < +m1) [m1, m2] = [m2, m1];
                r += `|${m1}`;
                if (m2 && m2 !== m1) r += `~${m2}`;
            }
            return r;
        }
        if (!d2) return '';
        return d22 ? `${d2}~${d22}` : String(d2);
    }

    /** 기종코드로 세부기종(ca1)을 찾는다 — 분류1 을 되짚을 때 쓴다 */
    findCa(code) {
        const row = $j.F.Car.row(code);
        return row ? row.ca1 : '';
    }

    // ─────────────────────────────────────────────────────────────
    // 내부
    // ─────────────────────────────────────────────────────────────

    /** idList 에 없는 칸은 빈 jQuery 객체 — 호출부가 존재여부를 따지지 않게 한다 */
    $el(key) {
        const id = this.idList[key];
        return id ? $('#' + id) : $();
    }

    val(key) {
        return this.$el(key).val() ?? '';
    }

    /**
     * 칸을 보이거나 감춘다.
     * 종료칸을 `<span class="cs-range"> ~ <select…></span>` 로 감싸 두면 앞의 `~` 까지
     * 함께 감춘다 — 감춘 칸 옆에 물결만 덩그러니 남지 않게.
     */
    slot(key, show) {
        const $e = this.$el(key);
        if (!$e.length) return;

        // 행에 `data-cs-row="<key>"` 를 달아 두면 라벨까지 통째로 감춘다
        // (안 그러면 감춘 칸 자리에 라벨만 덩그러니 남는다)
        const $row = $e.closest(`[data-cs-row="${key}"]`);
        if ($row.length) { $row.toggle(!!show); return; }

        const $wrap = $e.closest('.cs-range');
        ($wrap.length ? $wrap : $e).toggle(!!show);
    }

    getDepth1Title() {
        return $j.F.Car.isMeterType(this.getCarType()) ? '톤수' : '유형';
    }

    getDepth2Title() {
        const carType = this.getCarType();
        // 리프트는 ca2 가 톤이 아니라 미터다 (6m·8m…) — 스카이·사다리와 같은 표기를 쓴다
        return ($j.F.Car.isMeterType(carType) || carType === '리프트') ? '높이(미터)' : '톤수';
    }
}
